import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import { TUTOR_BASE_URL } from "@/utils/apiBaseUrls";

// Track in-flight stops per session so the beforeunload + pagehide pair (which
// can both fire on close) collapses to a single POST. Sequential re-calls after
// the request settles are allowed (and are harmless — the backend stop_lesson is
// idempotent once the session is cleaned up).
const _inFlight = new Set();
// Session id -> slide whose notepad tutor ended the lesson; the backend never
// sees that op. Sent once: session ids repeat when the lesson is reopened.
const _endedByTutor = new Map();

export const markLessonEndedByTutor = (slideIndex, sessionId = null) => {
  const id = sessionId ?? useSessionStore.getState().getSessionId();
  if (id && Number.isInteger(slideIndex)) _endedByTutor.set(id, slideIndex);
};

/**
 * POST /session/stop_lesson/{id}, which runs the server-side cleanup synchronously
 * (saves the final bite snapshot + pre-fills status to Postgres). Returns the
 * fetch promise so graceful-exit paths can AWAIT it before navigating to the
 * lessons list, closing the race where the list reads status before cleanup runs.
 *
 * Uses keepalive so it still delivers during page unload (beforeunload/pagehide),
 * where awaiting is impossible.
 *
 * @param {string|null} sessionId - defaults to the current session store value.
 * @returns {Promise<void>}
 */
export const stopLessonSession = (sessionId = null) => {
  const id = sessionId ?? useSessionStore.getState().getSessionId();
  if (!id) return Promise.resolve();
  if (_inFlight.has(id)) return Promise.resolve();
  _inFlight.add(id);

  const url = `${TUTOR_BASE_URL}/session/stop_lesson/${id}`;
  const endedSlide = _endedByTutor.get(id);
  _endedByTutor.delete(id);

  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(
      endedSlide === undefined
        ? {}
        : { navigated_by: "ai", slide_index: endedSlide },
    ),
    credentials: "include",
    keepalive: true,
  })
    .catch(() => {
      // Errors during unload are expected and can be ignored.
    })
    .finally(() => {
      _inFlight.delete(id);
    });
};
